import { useEffect, useMemo, useState, useContext } from 'react'
import { TableContext } from 'Context/Table'
import ActionCell from 'Presentation/Components/Table/Components/CustomActionCell'
import { useNavigate } from 'react-router-dom'
import { usePileStore } from 'Store/Pile'
import moment from 'moment'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'

const INITIAL_FILTER = {
  q: '',
  machineTypeID: null,
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { rawData, getAll, deleteMachine, clearState } = usePileStore(
    state => ({
      rawData: state.data,
      getAll: state.getAll,

      deleteMachine: state.delete,
      clearState: state.clearState,
    }),
    shallow
  )
  const {
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)

  const [filter, setFilter] = useState(INITIAL_FILTER)

  const data = useMemo(() => {
    return rawData
  }, [rawData])

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const fetchData = () => {
    updateLoading(true)
    updateError(false)

    getAll(filter).then(({ data, success }) => {
      if (!success) {
        updateLoading(false)
        updateError(true)
        toast.error('เกิดข้อผิดพลาดในการดึงข้อมูล : ' + data)
      } else {
        updateLoading(false)
        updateError(false)
      }
    })
  }

  const setupTable = () => {
    updateSorting('id', true)
    updatePagination(true)
  }

  const onAdd = () => {
    navigate('/pile/create')
  }

  const onView = (id: any) => {
    navigate(`/pile/${id}`)
  }

  const onDelete = (id: any) => {
    Swal.fire({
      title: 'ลบข้อมูลเสาเข็ม',
      text: 'คุณต้องการที่จะลบเสาเข็มนี้หรือไม่',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    }).then(async result => {
      if (result.isConfirmed) {
        const { data, success } = await deleteMachine(id)
        if (!success) {
          toast.error('เกิดข้อผิดพลาดในการลบข้อมูล : ' + data)
        } else {
          fetchData()
          toast.success('ลบข้อมูลสำเร็จ')
        }
      }
    })
  }

  const onClearFilter = () => {
    setFilter(INITIAL_FILTER)
    getAll(INITIAL_FILTER)
  }

  const onSubmitFilter = () => {
    fetchData()
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'ลำดับ',
      accessor: 'id',
      width: 50,
      minWidth: 50,
      Cell: ({ row }: any) => {
        return <span className='text-gray-800'>{row.index + 1}</span>
      },
    },
    {
      Header: 'ชื่อเสาเข็ม',
      accessor: 'itemTitle',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'รหัสเสาเข็ม',
      accessor: 'itemCode',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ความสูง',
      accessor: 'height',
      width: 70,
      minWidth: 70,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ความกว้าง',
      accessor: 'width',
      width: 70,
      minWidth: 70,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'น้ำหนัก',
      accessor: 'weight',
      width: 70,
      minWidth: 70,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'วันที่สร้าง',
      accessor: 'createdAt',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {moment(value).tz('Asia/Bangkok').format('DD/MM/YYYY HH:mm')}
          </span>
        )
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='d-flex justify-content-end'>
          <ActionCell
            id={row.original.id}
            hideEdit
            hideDuplicate
            onClickView={() => onView(row.original.id)}
            onClickDelete={() => onDelete(row.original.id)}
          />
        </div>
      ),
    },
  ]

  useEffect(() => {
    setupTable()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    return () => {
      clearState()
      resetTableState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    data,
    TABLE_CONFIGS,
    filter,
    onAdd,
    onChangeFilter,
    onSubmitFilter,
    onClearFilter,
  }
}

export default ViewModel
